import {useEffect, useMemo} from "react";
import {useSelector} from "react-redux";

import {SupportContactFormService} from "webform/gen/supportContactForm";
import {getActiveCitiesList} from "webform/slices/geoSlice";
import {geoCitiesSelector, isWebformAvailableSelector} from "webform/store/selectors";
import {useAppDispatch} from "webform/store/hooks";

import {type GroupedItems, type Item} from "webform/components";

export interface CityByCountryMap {
    [country: string]: Item[];
}

interface GeoDataState {
    countries: Item[];
    citiesByCountry: Array<GroupedItems<Item>>;
}

const getCitiesByCountry = (
    cities: SupportContactFormService.CityItem[],
    selectedCountry?: string,
): Array<GroupedItems<Item>> => {
    const citiesByCountryName = cities.reduce<Record<string, SupportContactFormService.CityItem[]>>((acc, city) => {
        (acc[city.country_name] ??= []).push(city);
        return acc;
    }, {});

    const sortedItems = Object.entries(citiesByCountryName).map(([countryName, citiesInCountry]) => ({
        id: countryName,
        title: countryName,
        items: citiesInCountry
            .map((city) => ({
                id: city.id.toString(),
                title: city.city_name,
            }))
            .sort((first, second) => first.title.localeCompare(second.title)),
    }));

    const found = sortedItems.find((item) => item.id === selectedCountry);

    if (found) {
        return [found];
    }
    return sortedItems;
};

const getCountries = (cities: SupportContactFormService.CityItem[]): Item[] => {
    const result = cities
        .map((item) => item.country_name)
        .filter((value, index, self) => self.indexOf(value) === index)
        .map((country) => ({id: country, title: country}));
    return result;
};

const countriesComparator = (first: Item, second: Item) => first.title.localeCompare(second.title);

export const useGeoData = (selectedCountry?: string) => {
    const dispatch = useAppDispatch();
    const cities = useSelector(geoCitiesSelector);
    const isWebformAvailable = useSelector(isWebformAvailableSelector);

    useEffect(() => {
        if (isWebformAvailable && !cities) {
            dispatch(getActiveCitiesList());
        }
    }, [cities, dispatch, isWebformAvailable]);

    // Derive the dropdown shapes from the fetched cities rather than mirroring them into
    // state via an effect (avoids the cascading-render setState-in-effect pattern).
    return useMemo<GeoDataState>(() => {
        if (!isWebformAvailable || !cities) {
            return {countries: [], citiesByCountry: []};
        }
        return {
            countries: getCountries(cities).sort(countriesComparator),
            citiesByCountry: getCitiesByCountry(cities, selectedCountry),
        };
    }, [cities, isWebformAvailable, selectedCountry]);
};
